import { SUPPORTED_CLERK_EVENTS } from '@/lib/integrations/inbound/providers/clerk/event-types'
import { INTEGRATION_BRAND_DOMAIN } from '@/lib/integrations/integration-brand-domains'
import { SUPPORTED_STRIPE_EVENTS } from '@/lib/integrations/oauth/stripe/event-types'
import { SUPABASE_SETUP_SQL } from './supabase-setup-sql'
import type { IntegrationDefinition, IntegrationProvider } from './types'

const CLERK_EVENT_COUNT = SUPPORTED_CLERK_EVENTS.length
const STRIPE_EVENT_COUNT = SUPPORTED_STRIPE_EVENTS.length

export const DATA_IN_INTEGRATION_DEFINITIONS = {
  framer: {
    name: 'Framer',
    brandDomain: INTEGRATION_BRAND_DOMAIN.framer,
    description:
      'Add your existing Framer forms to Brew. Submissions add or update contacts and can start automations using each form’s trigger.',
    shortDescription: 'Forms and signups',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'Brew',
    lastEditedAt: 'September 5, 2026',
    dataDirection: 'data_in',
    integrationType: 'form_webhook',
    tutorialDocsUrl:
      'https://www.framer.com/help/articles/framer-form-webhook-setup/',
    tutorialSteps: [
      {
        label: 'Add your Framer form',
        description:
          'Select your brand, open Framer, and choose Add form. Name your existing form and define the fields it submits. Each form becomes a Brew trigger.',
      },
      {
        label: 'Add the webhook in Framer',
        description:
          'Select your native form, open Send To, and add a Webhook. Paste the Brew URL and signing secret into the destination.',
      },
      {
        label: 'Match your field names',
        description:
          'Use email for the required email input. Match firstName, lastName, and any custom fields to the names shown in Brew.',
      },
      {
        label: 'Publish and submit',
        description:
          'Publish your Framer site and submit the form. Brew receives the fields and adds or updates the contact, even without a published automation.',
      },
      {
        label: 'Connect an automation',
        description:
          'Open the form’s Automations tab and choose Create automation. Its trigger is selected for you. Publish the automation to run it on future submissions.',
      },
    ],
  },
  clerk: {
    name: 'Clerk',
    brandDomain: INTEGRATION_BRAND_DOMAIN.clerk,
    description:
      'Trigger Brew automations from Clerk webhook events — user signups, organization changes, paid subscriptions, and waitlist entries.',
    shortDescription: 'Auth event triggers',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'May 23, 2026',
    dataDirection: 'data_in',
    integrationType: 'webhook_signing_secret',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/import/clerk',
    tutorialSteps: [
      {
        label: 'Generate your Brew webhook URL',
        description:
          'Click Generate URL below to mint a unique endpoint for this brand. Copy it for the next step.',
        input: {
          type: 'copy',
          key: 'webhookUrl',
          placeholder:
            'Click "Generate URL" above to mint your unique Brew endpoint',
        },
      },
      {
        label: 'Add an endpoint in Clerk',
        description:
          'Open dashboard.clerk.com → Webhooks → + Add Endpoint. Paste the Brew URL into the Endpoint URL field.',
      },
      {
        label: `Subscribe to the ${CLERK_EVENT_COUNT} events Brew supports`,
        description:
          'In Subscribe to events, enable the user, organization, organizationInvitation, organizationMembership, paymentAttempt, subscription, subscriptionItem, and waitlistEntry events listed in Trigger events (Overview tab). Then click Create.',
      },
      {
        label: 'Reveal the Signing Secret',
        description:
          'On the endpoint detail page Clerk shows next, click the eye icon next to Signing Secret to reveal it. The value starts with whsec_.',
      },
      {
        label: 'Paste the Signing Secret below',
        description:
          'Paste the whsec_… secret and click Connect. Brew verifies every webhook with the Standard Webhooks (Svix) spec.',
        input: {
          type: 'password',
          key: 'apiKey',
          placeholder: 'whsec_...',
        },
      },
    ],
  },
  figma: {
    name: 'Figma',
    brandDomain: INTEGRATION_BRAND_DOMAIN.figma,
    description:
      'Turn Figma frames into pixel-accurate, responsive emails — paste a frame URL and Brew handles the rest.',
    shortDescription: 'Design-to-email',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'May 12, 2026',
    dataDirection: 'data_in',
    integrationType: 'oauth',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/import/figma',
    oAuthProvider: 'figma',
    tutorialSteps: [
      {
        label: 'Connect your Figma account',
        description:
          'Authorize Brew to access your Figma files, then paste a Figma frame URL into chat.',
      },
    ],
  },
  slack: {
    name: 'Slack',
    brandDomain: INTEGRATION_BRAND_DOMAIN.slack,
    description:
      'Mention @Brew in any Slack channel, thread or DM to work with Brew as yourself; every thread becomes a Brew chat. Brew also posts sent emails, a weekly overview, and comment mentions to the channels you choose.',
    shortDescription: 'Iterate on emails from Slack',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'Jan 21, 2026',
    dataDirection: 'data_in',
    integrationType: 'oauth',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/import/slack',
    oAuthProvider: 'slack',
    tutorialSteps: [
      {
        label: 'Connect your workspace',
        description:
          'An organization admin clicks Connect on this page and approves Brew in Slack. One install covers the whole organization.',
      },
      {
        label: 'Choose notification channels',
        description:
          'Pick the channels this brand posts to, and which updates each one gets: emails sent, the weekly overview, comment mentions and replies.',
      },
      {
        label: 'Mention @Brew',
        description:
          'Mention @Brew in a channel or DM it. The first time, Brew sends you a private link to connect your own Brew account; after that it answers in the thread as you. An organization admin can turn chatting off.',
      },
    ],
  },
  typeform: {
    name: 'Typeform',
    brandDomain: INTEGRATION_BRAND_DOMAIN.typeform,
    description:
      'Inform email copy with the latest Typeform responses — Brew suggests messaging that mirrors recent feedback.',
    shortDescription: 'Forms & surveys',
    author: 'Brew',
    availability: 'coming_soon',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'Dec 8, 2024',
    dataDirection: 'data_in',
    integrationType: 'oauth',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/import/typeform',
  },
  stripe: {
    name: 'Stripe',
    brandDomain: INTEGRATION_BRAND_DOMAIN.stripe,
    description: `Sync Stripe customers into the brand audience, trigger automations from ${STRIPE_EVENT_COUNT} Stripe events, and ship pre-built lifecycle emails (welcome, trial-end, failed-payment recovery, win-back).`,
    shortDescription: 'Customer sync + event triggers',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'Brew Team',
    lastEditedAt: 'September 29, 2026',
    dataDirection: 'data_in',
    integrationType: 'oauth',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/import/stripe',
    oAuthProvider: 'stripe',
    tutorialSteps: [
      {
        label: 'Click Connect with Stripe in the sidebar',
        description:
          'You will be redirected to Stripe to authorize Brew (one click). Stripe verifies your identity — Brew never sees your password.',
      },
      {
        label: 'Approve the OAuth scopes',
        description:
          'Brew requests read access to customers, subscriptions, invoices, charges, refunds, payment intents, quotes, and checkout sessions. See the Security tab for the full scope list. You can revoke access at any time from your Stripe dashboard.',
      },
      {
        label: 'Brew provisions trigger rows automatically',
        description: `After OAuth completes Brew registers a Connect webhook with Stripe and seeds the ${STRIPE_EVENT_COUNT} supported event rows (customer.*, customer.subscription.*, invoice.*, checkout.session.*, quote.*, charge.refunded, refund.created, payment_intent.payment_failed) as available triggers. No manual webhook setup or event enable switch is required. Only Live automations run.`,
      },
      {
        label: 'Publish an automation for the event you need',
        description:
          'Create an automation using a Stripe event and publish it to make it Live. In Manage, use Sync customers to import historical customers. The per-event Sync customer name setting only controls name updates.',
      },
    ],
  },
  stytch: {
    name: 'Stytch',
    brandDomain: INTEGRATION_BRAND_DOMAIN.stytch,
    description:
      'Trigger Brew automations from identity-bearing Stytch user and member events. Brew records organization and SCIM connection events without a recipient as unresolved. Only DIRECT-source events are processed.',
    shortDescription: 'Auth event triggers',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'May 23, 2026',
    dataDirection: 'data_in',
    integrationType: 'webhook_signing_secret',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/import/stytch',
    tutorialSteps: [
      {
        label: 'Generate your Brew webhook URL',
        description:
          'Click Generate URL below to mint a unique endpoint for this brand. Copy it for step 3.',
        input: {
          type: 'copy',
          key: 'webhookUrl',
          placeholder:
            'Click "Generate URL" above to mint your unique Brew endpoint',
        },
      },
      {
        label: 'Pick your Stytch project type',
        description:
          'Choose Consumer (B2C) or B2B — Brew filters to events for the selected vertical. Webhooks require the B2B Pro tier; Consumer ships on all paid Stytch plans.',
        input: {
          type: 'select',
          key: 'stytchProject',
          options: [
            { value: 'consumer', label: 'Consumer (B2C)' },
            { value: 'b2b', label: 'B2B' },
          ],
        },
      },
      {
        label: 'Add an endpoint in Stytch',
        description:
          'Open stytch.com/dashboard → Webhooks → + Add Endpoint. Paste the Brew URL into the Endpoint URL field.',
      },
      {
        label: 'Filter to the events Brew supports',
        description:
          'Consumer projects: enable direct.user.create / update / delete. B2B projects: enable direct.organization.create plus direct.member.create / update / delete and direct.scim_connection.create. Click Create.',
      },
      {
        label: 'Reveal the Signing Secret',
        description:
          'On the endpoint settings page Stytch shows next, reveal the Signing Secret. The value starts with whsec_.',
      },
      {
        label: 'Paste the Signing Secret below',
        description:
          'Paste the whsec_… secret and click Connect. Brew verifies every webhook with the Standard Webhooks (Svix) spec.',
        input: {
          type: 'password',
          key: 'apiKey',
          placeholder: 'whsec_...',
        },
      },
    ],
  },
  supabase: {
    name: 'Supabase',
    brandDomain: INTEGRATION_BRAND_DOMAIN.supabase,
    description:
      'Trigger Brew automations from Supabase auth.users database webhooks — including derived events like email confirmation, password change, anonymous-to-real upgrade, and ban / unban.',
    shortDescription: 'Auth-event triggers',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'May 23, 2026',
    dataDirection: 'data_in',
    integrationType: 'webhook_signing_secret',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/import/supabase',
    tutorialSteps: [
      {
        label: 'Generate your Brew webhook URL',
        description:
          'Click Generate URL below to mint a unique endpoint for this brand. The SQL in step 3 will pre-fill it for you.',
        input: {
          type: 'copy',
          key: 'webhookUrl',
          placeholder:
            'Click "Generate URL" above to mint your unique Brew endpoint',
        },
      },
      {
        label: 'Create your shared signing secret',
        description:
          'You own this secret; Brew and Supabase do not issue it. Run `openssl rand -hex 32`, then paste the same value into the SQL block and the Connect form.',
      },
      {
        label: 'Run the setup SQL in Supabase',
        description:
          'In your Supabase project, open SQL Editor → New query. Paste the block below, replace `<PASTE_YOUR_SECRET_HERE>` with the secret from step 2, then click Run.',
        input: {
          type: 'code',
          language: 'sql',
          value: SUPABASE_SETUP_SQL,
        },
      },
      {
        label: 'Paste the same secret here',
        description:
          'Paste the secret from step 2 below, then click Connect. Brew uses it to verify every webhook your new trigger sends.',
        input: {
          type: 'password',
          key: 'apiKey',
          placeholder: 'Same secret you pasted into the SQL block',
        },
      },
    ],
  },
  workos: {
    name: 'WorkOS',
    brandDomain: INTEGRATION_BRAND_DOMAIN.workos,
    description:
      'Trigger Brew automations from WorkOS user, authentication, password-reset, organization, directory-sync (SCIM), connection, and invitation events.',
    shortDescription: 'Enterprise auth triggers',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'May 23, 2026',
    dataDirection: 'data_in',
    integrationType: 'webhook_signing_secret',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/import/workos',
    tutorialSteps: [
      {
        label: 'Generate your Brew webhook URL',
        description:
          'Click Generate URL below to mint a unique endpoint for this brand. Copy it for the next step.',
        input: {
          type: 'copy',
          key: 'webhookUrl',
          placeholder:
            'Click "Generate URL" above to mint your unique Brew endpoint',
        },
      },
      {
        label: 'Create an endpoint in WorkOS',
        description:
          'Open dashboard.workos.com → Webhooks → Create Endpoint. Paste the Brew URL into the Endpoint URL field.',
      },
      {
        label: 'Subscribe to the 18 events Brew supports',
        description:
          'Enable the user.*, authentication.*, password_reset.succeeded, organization.*, organization_membership.*, dsync.user.*, connection.activated, and invitation.accepted events listed in Trigger events (Overview tab). Then click Create.',
      },
      {
        label: 'Copy the endpoint Signing Secret',
        description:
          'WorkOS reveals the per-endpoint signing secret on the endpoint detail page. Copy it — the value is opaque (no prefix).',
      },
      {
        label: 'Paste the Signing Secret below',
        description:
          'Paste the secret and click Connect. Brew verifies every webhook with the WorkOS `t=<ts>,v1=<hmac>` envelope, 180s timestamp tolerance.',
        input: {
          type: 'password',
          key: 'apiKey',
          placeholder: 'WorkOS endpoint signing secret',
        },
      },
    ],
  },
  shopify: {
    name: 'Shopify',
    brandDomain: INTEGRATION_BRAND_DOMAIN.shopify,
    description:
      'Trigger Brew automations from Shopify customer, order, checkout, and fulfillment events. Powers welcome, receipt, abandoned-checkout, and shipping flows.',
    shortDescription: 'Commerce event triggers',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'May 23, 2026',
    dataDirection: 'data_in',
    integrationType: 'webhook_signing_secret',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/import/shopify',
    tutorialSteps: [
      {
        label: 'No client ID is required',
        description:
          'This setup uses Shopify Admin webhooks, not a Shopify app. You do not need a client ID, client secret, or OAuth app to connect this version of the integration.',
      },
      {
        label: 'Generate your Brew webhook URL',
        description:
          'Click Generate URL below to mint a unique endpoint for this brand. Copy it for step 4.',
        input: {
          type: 'copy',
          key: 'webhookUrl',
          placeholder:
            'Click "Generate URL" above to mint your unique Brew endpoint',
        },
      },
      {
        label: 'Enter your myshopify.com shop domain',
        description:
          'Use the canonical myshopify.com host (e.g. acme.myshopify.com), NOT your custom storefront domain. Brew matches this against the X-Shopify-Shop-Domain header to block cross-shop replay attacks.',
        input: {
          type: 'text',
          key: 'domain',
          placeholder: 'acme.myshopify.com',
        },
      },
      {
        label: 'Open Shopify Admin → Settings → Notifications',
        description:
          'Click Settings (gear icon, bottom-left of Shopify admin) → Notifications. Scroll to the Webhooks section.',
      },
      {
        label: 'Create one webhook per topic you want',
        description:
          'For each Brew-supported topic (customers/*, orders/*, checkouts/*, fulfillments/* — full list in Trigger events on the Overview tab), click Create webhook, set Format to JSON, paste the Brew URL, pick the latest API version, and Save. Do not add carts/update: Shopify does not include a recipient email in that payload.',
      },
      {
        label: 'Copy the per-shop signing secret',
        description:
          'Scroll to the bottom of the Webhooks section. Copy the long secret under "Your webhooks will be signed with…". The same secret signs every webhook on this shop.',
      },
      {
        label: 'Paste the signing secret below',
        description:
          'Paste the secret and click Connect. Brew verifies every webhook with base64(HMAC-SHA-256) against the X-Shopify-Hmac-SHA256 header.',
        input: {
          type: 'password',
          key: 'apiKey',
          placeholder: 'Shopify per-shop webhook signing secret',
        },
      },
    ],
  },
  revenuecat: {
    name: 'RevenueCat',
    brandDomain: INTEGRATION_BRAND_DOMAIN.revenuecat,
    description:
      'Trigger Brew automations from RevenueCat in-app purchase, subscription, and billing events across Apple, Google, Amazon, Stripe, and Web Billing. Requires the RevenueCat Pro plan or above.',
    shortDescription: 'IAP + subscription events',
    author: 'Brew',
    availability: 'available',
    createdByBrew: true,
    lastEditedBy: 'System',
    lastEditedAt: 'May 23, 2026',
    dataDirection: 'data_in',
    integrationType: 'webhook_signing_secret',
    tutorialDocsUrl: 'https://docs.brew.new/integrations/import/revenuecat',
    tutorialSteps: [
      {
        label: 'Generate your Brew webhook URL',
        description:
          'Click Generate URL below to mint a unique endpoint for this brand. RevenueCat webhooks require the Pro plan or above.',
        input: {
          type: 'copy',
          key: 'webhookUrl',
          placeholder:
            'Click "Generate URL" above to mint your unique Brew endpoint',
        },
      },
      {
        label: 'Create the webhook configuration',
        description:
          'In RevenueCat, open Integrations → Webhooks → Add new configuration. Paste the Brew Webhook URL, choose Production, Sandbox, or both, and select the lifecycle events you want to send.',
      },
      {
        label: 'Enable HMAC webhook signing',
        description:
          'Toggle HMAC webhook signing in that RevenueCat webhook configuration. Copy the signing secret immediately: it is shown only once at creation or rotation. Brew verifies X-RevenueCat-Webhook-Signature against the exact raw request body.',
      },
      {
        label: 'Paste the signing secret here',
        description:
          'Paste the HMAC signing secret and click Connect. Do not paste a RevenueCat REST API key or the optional Authorization-header value. Existing Authorization-only Brew connections remain supported temporarily.',
        input: {
          type: 'password',
          key: 'apiKey',
          placeholder: 'RevenueCat webhook signing secret',
        },
      },
      {
        label: 'Send a RevenueCat TEST event',
        description:
          'Use Send test webhook in RevenueCat. A signed TEST delivery verifies and activates the Brew connection; TEST itself does not trigger a customer automation. Real supported lifecycle events do.',
      },
      {
        label: 'Set the customer email attribute',
        description:
          'Set RevenueCat subscriber attribute $email for each customer so Brew can deliver automation emails. Brew also accepts an email-shaped app_user_id; otherwise the event is recorded but delivery uses a non-sendable synthetic address.',
      },
    ],
  },
} satisfies Partial<Record<IntegrationProvider, IntegrationDefinition>>
